import { fetchApi } from "../api";

// PII fields that need client-side encryption
const PII_FIELDS = ["TelNo", "MobNo", "um_mobile_no", "um_telephone_no", "cs_telephone_no", "cs_mobile_no"];

class KMSClientEncryption {
  constructor() {
    this.dataKey = null;
    this.encryptedDataKey = null;
  }

  async initializeDataKey() {
    const response = await fetchApi("/api/signature/generate-client-key");
    if (response?.plaintextKey && response?.encryptedKey) {
      this.dataKey = response.plaintextKey;
      this.encryptedDataKey = response.encryptedKey;
      return true;
    }
    return false;
  }

  isReady() {
    return this.dataKey !== null && this.encryptedDataKey !== null;
  }

  async encryptPIIFields(data, fields) {
    if (!this.isReady()) {
      await this.initializeDataKey();
    }

    const piiFields = fields || PII_FIELDS;
    const encryptedData = { ...data };

    for (const field of piiFields) {
      if (data[field]) {
        try {
          const encryptedValue = await this.encryptWithAES(data[field]);
          // Send as JSON string so backend stores it as String (S) type in DynamoDB
          encryptedData[field] = JSON.stringify({
            encryptedKey: this.encryptedDataKey,
            encryptedData: encryptedValue
          });
        } catch (error) {
          console.error(`Failed to encrypt field ${field}:`, error);
        }
      }
    }

    return encryptedData;
  }

  async decryptPIIFields(data, fields) {
    const piiFields = fields || PII_FIELDS;
    const decryptedData = { ...data };

    for (const field of piiFields) {
      const fieldValue = data[field];
      if (!fieldValue) continue;

      try {
        let parsed = fieldValue;
        
        // Handle JSON string format: "{\"encryptedKey\":\"...\",\"encryptedData\":\"...\"}"
        if (typeof fieldValue === 'string') {
          try {
            parsed = JSON.parse(fieldValue);
          } catch (e) {
            // Not JSON, might be legacy plain base64 string
            if (this.dataKey) {
              decryptedData[field] = await this.decryptWithAES(fieldValue);
            }
            continue;
          }
        }

        // Handle object format: { encryptedKey, encryptedData }
        if (parsed && typeof parsed === 'object' && parsed.encryptedKey && parsed.encryptedData) {
          if (!this.dataKey) {
            await this.initializeDataKey();
          }
          decryptedData[field] = await this.decryptWithAES(parsed.encryptedData);
        }
      } catch (error) {
        console.error(`Failed to decrypt field ${field}:`, error);
        decryptedData[field] = 'DECRYPTION_ERROR';
      }
    }

    delete decryptedData.encryptedDataKey;
    return decryptedData;
  }

  async decryptWithAES(encryptedData) {
    const keyData = Uint8Array.from(atob(this.dataKey), c => c.charCodeAt(0));

    const aesKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    // Extract IV (first 12 bytes) and ciphertext + auth tag
    const iv = combined.slice(0, 12);
    const cipherAndTag = combined.slice(12);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      cipherAndTag
    );

    return new TextDecoder().decode(decrypted);
  }

  async encryptWithAES(plaintext) {
    const keyData = Uint8Array.from(atob(this.dataKey), c => c.charCodeAt(0));

    const aesKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const encoder = new TextEncoder();
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      encoder.encode(plaintext)
    );

    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...combined));
  }
}

const kmsClientEncryption = new KMSClientEncryption();
export default kmsClientEncryption;
